// 選択中アイテム/フォルダの保存・復元と自動選択制御
import { ref, watch } from 'vue';
import { isFolderItem, filterVideoItems } from './useItemTypeGuards';

const STORAGE_KEYS = {
  folderId: 'exa_dashboard_folder_id',
  itemId: 'exa_dashboard_item_id',
};

const isMobileView = () => window.innerWidth < 768;

export const useSelection = (items, folders, selectedFolderId) => {
  const selectedItem = ref(null);
  const selectedIds = ref(new Set());
  const lastSelectedId = ref(null);
  const lastSelectedIndex = ref(null);

  const normalizeId = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  };

  const resolveIndex = (item, fallback = null) => {
    if (typeof fallback === 'number' && !Number.isNaN(fallback)) {
      return fallback;
    }
    const id = normalizeId(item?.id);
    if (!id) {
      return null;
    }
    const list = filterVideoItems(items.value);
    const index = list.findIndex((entry) => normalizeId(entry?.id) === id);
    return index >= 0 ? index : null;
  };

  const storeFolderSelection = (folderId) => {
    try {
      window.localStorage.setItem(STORAGE_KEYS.folderId, String(folderId ?? ''));
    } catch (error) {
      // ignore storage errors
    }
  };

  const storeItemSelection = (itemId) => {
    try {
      window.localStorage.setItem(STORAGE_KEYS.itemId, String(itemId ?? ''));
    } catch (error) {
      // ignore storage errors
    }
  };

  const syncSelectedItem = (preferredItem = null) => {
    const ids = selectedIds.value;
    if (!ids.size) {
      selectedItem.value = null;
      storeItemSelection('');
      return;
    }

    if (preferredItem && ids.has(normalizeId(preferredItem.id))) {
      selectedItem.value = preferredItem;
      storeItemSelection(preferredItem.id);
      return;
    }

    const currentId = normalizeId(selectedItem.value?.id);
    if (currentId && ids.has(currentId)) {
      storeItemSelection(selectedItem.value?.id ?? '');
      return;
    }

    const fallbackId =
      (lastSelectedId.value && ids.has(lastSelectedId.value) && lastSelectedId.value) ||
      Array.from(ids)[0];
    const list = filterVideoItems(items.value);
    const found = list.find((entry) => normalizeId(entry?.id) === fallbackId);
    selectedItem.value = found || null;
    storeItemSelection(selectedItem.value?.id ?? '');
  };

  const clearItemSelection = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEYS.itemId);
    } catch (error) {
      // ignore storage errors
    }
    selectedItem.value = null;
    selectedIds.value = new Set();
    lastSelectedId.value = null;
    lastSelectedIndex.value = null;
  };

  const selectItem = (item, index = null) => {
    if (!item) {
      clearItemSelection();
      return;
    }
    if (isFolderItem(item)) {
      return;
    }
    const id = normalizeId(item.id);
    if (!id) {
      return;
    }
    selectedIds.value = new Set([id]);
    selectedItem.value = item;
    lastSelectedId.value = id;
    lastSelectedIndex.value = resolveIndex(item, index);
    storeItemSelection(item.id);
  };

  const toggleItemSelection = (item, index = null) => {
    if (!item) {
      return;
    }
    if (isFolderItem(item)) {
      return;
    }
    const id = normalizeId(item.id);
    if (!id) {
      return;
    }
    const next = new Set(selectedIds.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    selectedIds.value = next;
    lastSelectedId.value = id;
    lastSelectedIndex.value = resolveIndex(item, index);
    syncSelectedItem(item);
  };

  // shift範囲選択(フォルダ+動画混在行から動画分のみ)を選択状態へ反映する。
  // clickedVideoがnull(フォルダ行クリック)の場合はselectedIds/lastSelected*を温存し、
  // selectedItemのみクリアする(selectItem(null)=clearItemSelectionは選択を全消しするため使わない)
  const applyRangeSelection = (videoItems, { clickedVideo = null, append = false } = {}) => {
    const next = append ? new Set(selectedIds.value) : new Set();
    (Array.isArray(videoItems) ? videoItems : []).forEach((entry) => {
      const entryId = normalizeId(entry?.id);
      if (entryId) {
        next.add(entryId);
      }
    });
    selectedIds.value = next;

    if (clickedVideo) {
      selectedItem.value = clickedVideo;
      lastSelectedId.value = normalizeId(clickedVideo.id);
      lastSelectedIndex.value = resolveIndex(clickedVideo, null);
      storeItemSelection(clickedVideo.id);
      return;
    }

    selectedItem.value = null;
    storeItemSelection('');
  };

  const isItemSelected = (item) => {
    if (!item) {
      return false;
    }
    const id = normalizeId(item.id);
    if (!id) {
      return false;
    }
    return selectedIds.value.has(id);
  };

  const restoreSelection = ({ itemOnly = false } = {}) => {
    if (isMobileView()) {
      return;
    }
    let storedFolderId = null;
    let storedItemId = null;
    try {
      storedFolderId = window.localStorage.getItem(STORAGE_KEYS.folderId);
      storedItemId = window.localStorage.getItem(STORAGE_KEYS.itemId);
    } catch (error) {
      storedFolderId = null;
      storedItemId = null;
    }

    if (
      !itemOnly &&
      storedFolderId &&
      (folders.value || []).some((folder) => String(folder.id) === String(storedFolderId))
    ) {
      selectedFolderId.value = storedFolderId;
    }

    if (storedItemId) {
      const found = (items.value || []).find(
        (entry) => !isFolderItem(entry) && String(entry.id) === String(storedItemId)
      );
      if (found) {
        const id = normalizeId(found.id);
        selectedIds.value = new Set([id]);
        selectedItem.value = found;
        lastSelectedId.value = id;
        lastSelectedIndex.value = resolveIndex(found, null);
        storeItemSelection(found.id);
      }
    }
  };

  const clearSelection = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEYS.folderId);
      window.localStorage.removeItem(STORAGE_KEYS.itemId);
    } catch (error) {
      // ignore storage errors
    }
    selectedItem.value = null;
    selectedIds.value = new Set();
    lastSelectedId.value = null;
    lastSelectedIndex.value = null;
  };

  watch(
    items,
    (list) => {
      const currentList = Array.isArray(list) ? list : [];
      const currentIds = new Set(
        currentList.map((entry) => normalizeId(entry?.id)).filter((id) => id !== '')
      );
      const nextIds = new Set(
        Array.from(selectedIds.value).filter((id) => currentIds.has(id))
      );
      selectedIds.value = nextIds;

      if (selectedItem.value && !currentIds.has(normalizeId(selectedItem.value.id))) {
        selectedItem.value = null;
      }
      if (!selectedItem.value && nextIds.size) {
        syncSelectedItem();
      }
      if (!nextIds.size) {
        selectedItem.value = null;
      }
    },
    { immediate: true }
  );

  return {
    selectedItem,
    selectedIds,
    selectItem,
    toggleItemSelection,
    applyRangeSelection,
    isItemSelected,
    restoreSelection,
    clearItemSelection,
    clearSelection,
    storeFolderSelection,
  };
};
